const ROOT = '/images/configurator/rasters-v2';
const images = new Map();
export const masterSource = (slug) => `${ROOT}/${slug}.webp`;

function loadImage(src) {
  if (!images.has(src))
    images.set(
      src,
      new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = () => {
          images.delete(src);
          reject(new Error('Image indisponible'));
        };
        image.src = src;
      }),
    );
  return images.get(src);
}
function pixels(image, size) {
  const surface = document.createElement('canvas');
  surface.width = surface.height = size;
  const context = surface.getContext('2d', { willReadFrequently: true });
  context.drawImage(image, 0, 0, size, size);
  return context.getImageData(0, 0, size, size);
}
// Color only the model-specific cloth matte. Every unmasked source pixel is
// preserved; a raster embroidery layer restores the original stitch edges.
export function tintFabric(source, mask, hex) {
  const target = [1, 3, 5].map((start) =>
    parseInt(hex.slice(start, start + 2), 16),
  );
  const result = new Uint8ClampedArray(source);
  let sum = 0,
    weights = 0;
  for (let i = 0; i < source.length; i += 4) {
    const weight = mask[i] / 255;
    sum +=
      (0.2126 * source[i] + 0.7152 * source[i + 1] + 0.0722 * source[i + 2]) *
      weight;
    weights += weight;
  }
  const mean = sum / Math.max(weights, 1);
  for (let i = 0; i < source.length; i += 4) {
    const weight = mask[i] / 255;
    if (!weight) continue;
    const luminance =
      0.2126 * source[i] + 0.7152 * source[i + 1] + 0.0722 * source[i + 2];
    const light = 0.35 + (0.65 * luminance) / Math.max(mean, 1);
    for (let channel = 0; channel < 3; channel++) {
      result[i + channel] =
        source[i + channel] * (1 - weight) +
        Math.min(255, target[channel] * light) * weight;
    }
  }
  return result;
}

export async function renderBookmark({ slug, front, texture, view }) {
  const size = 1024;
  const surface = document.createElement('canvas');
  surface.width = surface.height = size;
  const context = surface.getContext('2d');
  if (view === 'back') {
    const [cotton, mask, depth, accessories] = await Promise.all([
      loadImage(texture),
      loadImage(`${ROOT}/${slug}-back-mask.png`),
      loadImage(`${ROOT}/${slug}-back-depth.png`),
      loadImage(`${ROOT}/${slug}-accessories.webp`),
    ]);
    const cloth = pixels(cotton, size),
      matte = pixels(mask, size).data,
      lighting = pixels(depth, size).data;
    for (let i = 0; i < cloth.data.length; i += 4) {
      const shade = 0.77 + (0.23 * lighting[i]) / 255;
      for (let c = 0; c < 3; c++) cloth.data[i + c] *= shade;
      cloth.data[i + 3] = matte[i];
    }
    context.putImageData(cloth, 0, 0);
    context.drawImage(accessories, 0, 0, size, size);
    // The reverse has the mirrored silhouette, but no trace of the front face.
    const reversed = document.createElement('canvas');
    reversed.width = reversed.height = size;
    const reversedContext = reversed.getContext('2d');
    reversedContext.translate(size, 0);
    reversedContext.scale(-1, 1);
    reversedContext.drawImage(surface, 0, 0);
    return reversed;
  }
  const master = await loadImage(masterSource(slug));
  if (!/^#[0-9a-f]{6}$/i.test(front?.hex || '')) {
    context.drawImage(master, 0, 0, size, size);
    return surface;
  }
  const [mask, face] = await Promise.all([
    loadImage(`${ROOT}/${slug}-front-mask.png`),
    loadImage(`${ROOT}/${slug}-face-original.png`),
  ]);
  const source = pixels(master, size);
  source.data.set(tintFabric(source.data, pixels(mask, size).data, front.hex));
  context.putImageData(source, 0, 0);
  context.drawImage(face, 0, 0, size, size);
  return surface;
}
